/**
 * Domain configuration utilities for environment-aware URL handling
 * Provides consistent domain and URL generation across different deployment environments
 */

/**
 * Get the application domain based on environment and deployment context
 * Prioritizes explicit domains for production, uses Vercel URLs for previews,
 * and falls back to window.location.origin for development
 */
export const getAppDomain = (): string => {
  // Preview/Staging: prioritize Vercel's URL for preview deployments
  // This must come BEFORE production check since previews also run in production mode
  if (import.meta.env.VITE_VERCEL_URL && import.meta.env.VITE_VERCEL_ENV === 'preview') {
    const vercelUrl = import.meta.env.VITE_VERCEL_URL;
    // Ensure https:// prefix for Vercel URLs
    return vercelUrl.startsWith('http') ? vercelUrl : `https://${vercelUrl}`;
  }

  // Production: use explicit domain when set (only for actual production deployments)
  if (import.meta.env.MODE === 'production' &&
      import.meta.env.VITE_APP_DOMAIN &&
      import.meta.env.VITE_VERCEL_ENV === 'production') {
    return import.meta.env.VITE_APP_DOMAIN;
  }

  // Fallback: use Vercel URL if available (for any preview without explicit env)
  if (import.meta.env.VITE_VERCEL_URL) {
    const vercelUrl = import.meta.env.VITE_VERCEL_URL;
    return vercelUrl.startsWith('http') ? vercelUrl : `https://${vercelUrl}`;
  }

  // Development: use explicit domain or localhost
  if (import.meta.env.DEV) {
    return import.meta.env.VITE_APP_DOMAIN || 'http://localhost:8080';
  }

  // Runtime fallback: use current origin (useful for dynamic environments)
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  // Server-side fallback (should not happen in client-side React app)
  return 'https://propelpathways.co.uk';
};

/**
 * Get environment-specific email addresses
 */
export const getContactEmail = (): string => {
  return import.meta.env.VITE_CONTACT_EMAIL || 'info@propelpathways.co.uk';
};

/**
 * Get environment-specific status page URL
 */
export const getStatusPageUrl = (): string => {
  return import.meta.env.VITE_STATUS_PAGE_URL || 'https://status.propelpathways.co.uk';
};
